import { BfbtcReadAbi, BITFI_FEE_PRECISION, TotalSupplyAbi } from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * bfBTC — a BTC receipt that prices itself through an INVERTED ratio.
 *
 * Not ERC-4626 in any part (`asset()`, `totalAssets()`, `convertToAssets()` all
 * revert), and unlike every other bespoke reader here the share token is also
 * the staking contract: deposits, withdrawal requests, fees and the price all
 * live at one address.
 *
 * **`currentRatio()` counts the wrong way round.** It is bfBTC-per-BTC on a
 * 1e8 scale and it only ever FALLS — 98309083 at genesis, 90159130 500 days
 * later. The holder's rate is the reciprocal, `1e8 / ratio`, so a reader that
 * passed the number through unchanged would publish a share price that
 * declines while the position grows.
 *
 * The scale is fixed at 1e8 regardless of what the underlying is: the contract
 * converts to the underlying's decimals only in `previewWithdraw`, which is
 * why that view answers 8 decimals on Ethereum and 18 on BNB for the same
 * input. Nothing here reads it — the normalised `exchangeRate` is
 * decimal-free.
 *
 * **The withdrawal fee is where a disabled route hides.** `feeConfigs(0)` is
 * the exit to this EVM chain and `feeConfigs(1)` the exit to the Bitcoin
 * network, and BitFi switches a route off by making its fee unpayable rather
 * than by any flag: `percentageFee == BITFI_FEE_PRECISION` (100 %) on Base,
 * Core, TAC, GOAT and Pharos, and `fixedFee == type(uint128).max` on Hemi's
 * and Bitlayer's native leg. `previewWithdraw` then answers 0 with no error.
 * Both are surfaced as {@link BitfiExitState} so the row and the action route
 * read the same fact.
 *
 * Deposits carry the mirror of that: `minDepositTokenAmount` is a real minimum
 * (0.0001 BTC) on the chains that accept one, and ~`uint256.max/100` on the
 * chains that do not — which is how Core, TAC and GOAT are closed. It becomes
 * `depositCapacity: 0n`, so the row states the vault cannot be entered instead
 * of advertising a rate with no way in.
 */

/** 1e8 — the fixed scale of `currentRatio()` / `ratio(epoch)`. */
export const BITFI_RATIO_SCALE = 10n ** 8n

/**
 * A minimum this large is not a minimum, it is a closed door: the deployments
 * that do not accept deposits publish `minDepositTokenAmount` just under
 * `type(uint256).max / 100` rather than pausing. Anything above one whole BTC
 * at 18 decimals is far past any real floor (the live ones are 1e4 / 1e14).
 */
const CLOSED_DEPOSIT_MIN = 10n ** 20n

/**
 * A flat withdrawal fee of one whole bfBTC or more is not a fee, it is a
 * closed route: the live flat fees are 600–9,000 (0.000006–0.00009 bfBTC), and
 * the value BitFi actually writes to shut a leg is `type(uint128).max`. The
 * threshold sits five orders of magnitude above the real fees and thirty
 * below the sentinel, so neither can drift into the other.
 */
const CLOSED_FLAT_FEE = 10n ** 8n

export const readerBitfiBfbtc = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  const shareUnit = 10n ** BigInt(entry.decimals)
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: entry.address, name: 'currentRatio', params: [] },
      { address: entry.address, name: 'feeConfigs', params: [0] },
      { address: entry.address, name: 'feeConfigs', params: [1] },
      { address: entry.address, name: 'minDepositTokenAmount', params: [] },
      { address: entry.address, name: 'paused', params: [] },
    ],
    abis: [
      TotalSupplyAbi,
      BfbtcReadAbi,
      BfbtcReadAbi,
      BfbtcReadAbi,
      BfbtcReadAbi,
      BfbtcReadAbi,
    ],
    parse: ([supply, ratio, evmFee, nativeFee, minDeposit, paused]) => {
      const totalSupply = toBigInt(supply)
      const currentRatio = toBigInt(ratio)
      if (totalSupply === undefined) return undefined
      // A zero or missing ratio is a broken read, not a free token — dropping
      // the entry is the only safe answer, since every downstream number is a
      // division by it.
      if (currentRatio === undefined || currentRatio <= 0n) return undefined

      // Reciprocal, normalised to 1e18: 1 bfBTC → X BTC.
      const exchangeRate = (ONE_E18 * BITFI_RATIO_SCALE) / currentRatio
      const totalAssets =
        (totalSupply * exchangeRate * underlyingUnit) / (ONE_E18 * shareUnit)

      // `feeConfigs` returns a 2-tuple; multicall hands it back as an array.
      const feeOf = (
        cell: unknown,
      ): { pct: bigint; fixed: bigint } | undefined => {
        if (!Array.isArray(cell)) return undefined
        const pct = toBigInt(cell[0])
        const fixed = toBigInt(cell[1])
        return pct === undefined || fixed === undefined
          ? undefined
          : { pct, fixed }
      }
      const evm = feeOf(evmFee)
      const native = feeOf(nativeFee)

      // BitFi closes a route on EITHER dial: a 100 % percentage fee (Base,
      // Core, TAC, GOAT, Pharos — the EVM leg) or a `type(uint128).max` fixed
      // fee (Hemi, Bitlayer — the Bitcoin leg). Both make `requestWithdraw`
      // revert on the fee-vs-amount check, and `previewWithdraw` answers 0 for
      // both without erroring. An unreadable fee is treated as ENABLED so an
      // RPC hiccup cannot render a live vault as inescapable; the action route
      // re-reads it and REFUSES when it cannot, which is the safe direction
      // for the leg that actually moves money.
      const routeOpen = (f: { pct: bigint; fixed: bigint } | undefined) =>
        f === undefined ||
        (f.pct < BITFI_FEE_PRECISION && f.fixed < CLOSED_FLAT_FEE)
      const evmExitEnabled = routeOpen(evm)
      const nativeExitEnabled = routeOpen(native)
      const evmPct = evm?.pct

      const minDep = toBigInt(minDeposit)
      const depositsClosed =
        minDep !== undefined && minDep >= CLOSED_DEPOSIT_MIN

      const isPaused = paused === true

      return {
        totalAssets,
        totalSupply,
        exchangeRate,
        // The only exit is a request, and BitFi charges no PROPORTIONAL fee on
        // the enabled leg — the cost is a flat per-request amount (0.000006 to
        // 0.00009 bfBTC, per chain) that basis points cannot express. Publish
        // the honest 0 rather than a size-dependent number invented here; the
        // flat fee is stated on the row's description and enforced by the
        // action route, which reads `previewWithdraw` for the exact figure.
        withdrawFeeBps:
          evmExitEnabled && evmPct !== undefined
            ? Number((evmPct * 10_000n) / BITFI_FEE_PRECISION)
            : undefined,
        // Neither route settles in this block, so there is no instant leg to
        // enable — but a paused contract or two dead routes IS a closed exit,
        // and that must not read as an ordinary queue.
        instantRedeemEnabled: false,
        depositCapacity: depositsClosed || isPaused ? 0n : undefined,
        bitfiExit: {
          evmExitEnabled: evmExitEnabled && !isPaused,
          nativeExitEnabled: nativeExitEnabled && !isPaused,
          paused: isPaused,
        },
      }
    },
  }
}
